import colorNoneSvg from '@/assets/color-none.svg'
import { cn } from '@/utils'

type ColorIndicatorKind = 'text' | 'fill' | 'stroke'

interface ColorIndicatorProps {
  kind: ColorIndicatorKind
  /** CSS color string, or null for the "none" (diagonal-slash) state. */
  color: string | null
}

/** White blends into the panel, so its fill chip gets the same outline as the popover swatches. */
function isWhite(color: string): boolean {
  const c = color.trim().toLowerCase()

  return c === '#ffffff' || c === '#fff'
}

/** 20×20 indicator showing the current color for one target (text, fill, or stroke). */
export function ColorIndicator({ kind, color }: ColorIndicatorProps) {
  if (kind === 'text') {
    return (
      <div className="relative w-5 h-5 overflow-hidden">
        <div className="absolute flex flex-col items-center gap-px left-[2px] top-px">
          <span className="text-foreground text-xs font-bold leading-none select-none">T</span>
          <div
            className="h-1 w-4 rounded-[1px]"
            style={{ backgroundColor: color ?? 'hsl(var(--foreground))' }}
          />
        </div>
      </div>
    )
  }

  if (color === null) {
    // Not natively draggable: a browser image drag would cancel the Fill button's swatch drag.
    return <img src={colorNoneSvg} alt="None" className="block w-5 h-5" draggable={false} />
  }

  if (kind === 'fill') {
    return (
      <div
        className={cn('w-5 h-5 rounded-[2px]', isWhite(color) && 'border border-foreground')}
        style={{ backgroundColor: color }}
      />
    )
  }

  return (
    <div className="w-5 h-5 rounded-[2px] border-4 bg-transparent" style={{ borderColor: color }} />
  )
}
